
import React from 'react';

export const KeyFinancialTerms: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Essential Financial Terms Every Seller Should Know
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Master these critical financial terms to navigate your apartment building sale with confidence and maximize your returns.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/15c08a00db3c6ffaa98edbb0854385ea.jpg"
              alt="NOI calculation and apartment building financial analysis"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Net Operating Income (NOI)</h3>
              <p className="text-gray-600 mb-6">
                NOI is the most critical metric in apartment building sales. It represents your property's annual rental income 
                minus all operating expenses, but excludes mortgage payments, depreciation, and capital improvements.
              </p>
              
              <div className="bg-teal-50 p-6 rounded-xl mb-6">
                <h4 className="text-lg font-semibold text-teal-800 mb-4">NOI Calculation Formula:</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-teal-200 pb-1">
                    <span className="text-gray-700">Gross Rental Income</span>
                    <span className="font-medium">$120,000</span>
                  </div>
                  <div className="flex justify-between border-b border-teal-200 pb-1">
                    <span className="text-gray-700">- Vacancy Loss (5%)</span>
                    <span className="font-medium">-$6,000</span>
                  </div>
                  <div className="flex justify-between border-b border-teal-200 pb-1">
                    <span className="text-gray-700">- Operating Expenses</span>
                    <span className="font-medium">-$36,000</span>
                  </div>
                  <div className="flex justify-between font-bold text-teal-800 pt-2">
                    <span>Net Operating Income</span>
                    <span>$78,000</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>Why NOI Matters:</strong> Buyers use NOI to calculate cap rates and determine how much they're willing to pay. 
                  A higher NOI directly translates to a higher property value.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Capitalization Rate (Cap Rate)</h3>
              <p className="text-gray-600 mb-6">
                The cap rate is the expected rate of return on a real estate investment, expressed as a percentage. 
                It's calculated by dividing the NOI by the property's market value or purchase price.
              </p>
              
              <div className="bg-green-50 p-6 rounded-xl mb-6">
                <h4 className="text-lg font-semibold text-green-800 mb-4">Cap Rate Formula & Example:</h4>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-800 mb-2">Cap Rate = NOI ÷ Property Value</div>
                  <div className="text-lg text-gray-700 mb-4">$78,000 ÷ $1,000,000 = 7.8%</div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-red-600 font-bold">4-6%</div>
                    <div className="text-gray-600">Premium Areas</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-yellow-600 font-bold">6-8%</div>
                    <div className="text-gray-600">Average Markets</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-green-600 font-bold">8%+</div>
                    <div className="text-gray-600">Higher Risk</div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>Market Impact:</strong> Lower cap rates indicate higher property values. A 1% decrease in cap rate 
                  can increase your property value by 12-15%.
                </p>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=cap%20rate%20calculation%20chart%20with%20apartment%20building%20investment%20returns%2C%20commercial%20real%20estate%20cap%20rate%20comparison%20across%20different%20markets%2C%20professional%20investment%20analysis%20with%20percentage%20calculations%20and%20market%20data&width=600&height=400&seq=cap-rate-analysis-chart&orientation=landscape"
              alt="Cap rate calculation and market comparison analysis"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Additional Key Financial Terms</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Appreciation</h4>
              <p className="text-gray-600 mb-3">
                The increase in property value over time due to market conditions, improvements, or inflation. 
                Historical appreciation helps project future value.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Average: 3-5% annually
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Gross Rent Multiplier (GRM)</h4>
              <p className="text-gray-600 mb-3">
                A quick valuation tool calculated by dividing the property's price by its gross annual rental income. 
                Lower GRM typically indicates better value.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Formula: Price ÷ Gross Annual Rent
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Cash-on-Cash Return</h4>
              <p className="text-gray-600 mb-3">
                Measures the annual cash flow return on the actual cash invested. Important for leveraged 
                properties with mortgage financing.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Formula: Annual Cash Flow ÷ Cash Invested
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Debt Service Coverage Ratio (DSCR)</h4>
              <p className="text-gray-600 mb-3">
                Measures the property's ability to service its debt. Lenders typically require a DSCR of 1.20 or higher 
                for apartment building loans.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Formula: NOI ÷ Annual Debt Service
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Effective Gross Income (EGI)</h4>
              <p className="text-gray-600 mb-3">
                Total potential rental income minus vacancy and credit losses, plus other income sources 
                like laundry, parking, or storage fees.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Formula: Gross Income - Vacancy Loss + Other Income
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Operating Expense Ratio (OER)</h4>
              <p className="text-gray-600 mb-3">
                The percentage of gross income consumed by operating expenses. Lower ratios indicate more 
                efficient property management and higher profitability.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Formula: Operating Expenses ÷ Gross Income
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Understanding These Terms Gives You Power</h3>
            <p className="text-lg opacity-90 leading-relaxed">
              When you understand NOI, cap rates, and other key metrics, you can better evaluate offers, negotiate effectively, 
              and make informed decisions about timing your sale. Knowledge of these terms also helps you spot red flags in 
              buyer proposals and ensures you're getting fair market value for your property.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
